import { useMemo } from 'react'
import { IconCheck, IconX } from '@tabler/icons-react'
import Image from 'next/image'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import {
  DeliveryMethod,
  mapAvailableSlotToCalendarAvailbility,
  useQueryDeliverySlots
} from 'modules/delivery'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { Tooltip } from 'shared/ui/Tooltip'
import { cssColors } from 'shared/ui/utils/cssTokens'

import { ProductAvailabilityPlan } from '../../types/products'
import {
  mapProductAvailabilityPlantoCalendarAvailbility
} from '../../utils/products'

import styles from './DeliveryMethodBox.module.scss'

const MAX_DISPLAYED_SLOTS = 3

interface DeliveryMethodBoxProps {
  deliveryMethod: DeliveryMethod
  calendar: ProductAvailabilityPlan
}

export default function DeliveryMethodBox({
  deliveryMethod,
  calendar
}: DeliveryMethodBoxProps) {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const { data: deliverySlots, isLoading: isLoadingDeliverySlots } =
    useQueryDeliverySlots(deliveryMethod.id, token)

  const formatedCalendarAvailbility = useMemo(
    () => mapProductAvailabilityPlantoCalendarAvailbility(calendar),
    [calendar]
  )

  const slots = useMemo(() => {
    if (!deliverySlots) return []

    return mapAvailableSlotToCalendarAvailbility({
      deliverySlots,
      calendarAvailbility: formatedCalendarAvailbility,
      displayedSlots: MAX_DISPLAYED_SLOTS
    })
  }, [deliverySlots, formatedCalendarAvailbility])

  return (
    <div className={styles.deliveryMethodWrapper} aria-label={deliveryMethod.name}>
      {deliveryMethod.description && (
        <div className={styles.tooltipWrapper} aria-label={deliveryMethod.description}>
          <Tooltip
            title={deliveryMethod.description}
            placement='top'
            id={`deliveryMethod-box-${deliveryMethod.id}-tooltip`}
          >
            <button className={styles.questionMarkButton}>?</button>
          </Tooltip>
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.imageContainer}>
          {deliveryMethod.image ? (
            <Image
              src={deliveryMethod.image}
              alt={deliveryMethod.name}
              layout='fill'
              objectFit='contain'
            />
          ) : (
            <p>{deliveryMethod.name}</p>
          )}
        </div>
      </div>
      <div className={styles.slots}>
        {slots.length > 0
          ? slots.map(({ dateKey, dayName, date, isAvailable }) => {
              return (
                <p className={styles.slot} key={dateKey}>
                  <b>{dayName}</b>
                  <span>{date}</span>
                  {isAvailable ? (
                    <IconCheck
                      size={15}
                      color={cssColors.system.success.text}
                      aria-label='Dostępny'
                    />
                  ) : (
                    <IconX
                      size={15}
                      color={cssColors.system.error.text}
                      aria-label='Niedostępny'
                    />
                  )}
                </p>
              )
            })
          : isLoadingDeliverySlots && (
              <div className={styles.loaderSlotWrapper}>
                <LoadingIndicator
                  style={{ color: cssColors.brand.secondary }}
                  size={11}
                />
              </div>
            )}
      </div>
    </div>
  )
}
